import type { BlobModel } from '@/models/blob';
import { FormModel, type NetworkForm, type StudyFormEnforcementsPatch, type PostForm, type PostFormFile,
  type FormFileSignedResponse, type PresignedPostFields, type PresignedPost,
  type PostFormResponse, type CloneFormResponse} from '@/models/form';
import { signedApiClient } from '@/services/apiClient';
import axios, { type AxiosProgressEvent } from 'axios';

class FormService {

  private surveyEndpoint: string = '';
  initService(surveyEndpoint: string) {
    this.surveyEndpoint = surveyEndpoint;
  }

  /**
   * The study's forms, its own and its shared forms, in the study's form order. The API sorts them,
   * so the order they come back in is the order to show (docs/forms/REVIEW.md F4, decision 56).
   */
  async getStudyForms(id: string) {
    const uri = `study/${id}/forms`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    const formList: NetworkForm[] = response.data.forms;
    return formList.map(form => FormModel.mapFromBackend(form));
  }

  /**
   * Whether `caseId` may add an entry to each of the study's forms, by form id. The same route as
   * the study's forms read, which with `?caseId=` computes `enforcements.allowEntry` from that case's
   * entries (only for a form with effects). Everything else in the answer is the study's, so it is
   * dropped here.
   */
  async getCaseAllowEntry(studyId: string, caseId: string): Promise<Record<string, boolean>> {
    const uri = `study/${studyId}/forms?caseId=${caseId}`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    const formList: NetworkForm[] = response.data.forms;
    return Object.fromEntries(formList.map(form => {
      const { identifier, enforcements } = FormModel.mapFromBackend(form);
      return [identifier, enforcements?.allowEntry ?? true];
    }));
  }

  /**
   * Delete `formIds`, forms `studyId` owns, in one request, and answer with the ids deleted. The API
   * deletes all of them or none: a shared form or another study's form is a 400, a missing one a 404,
   * and a form another study holds entries on, or whose fields another form's entry rules read, a 409
   * whose `blocked` names each (`readDeleteRefusal` in `formDeletion.ts`).
   */
  async deleteStudyForms(studyId: string, formIds: string[]): Promise<string[]> {
    const uri = `study/${studyId}/forms/delete`;
    const apiClient = signedApiClient('POST', uri);
    return (await apiClient.post(uri, { formIds })).data.deletedFormIds;
  }

  /**
   * Copy form `id` into `studyId`, the study it belongs to. The API answers 404 for a form that
   * study doesn't own, a shared form among them, and 403 without `MANAGE_FORMS` there.
   */
  async cloneForm(id: string, studyId: string): Promise<CloneFormResponse> {
    const uri = `form/${id}/clone`;
    const apiClient = signedApiClient('POST', uri);
    return (await apiClient.post(uri, { studyId })).data;
  }

  /**
   * Save `studyId`'s form order and its own forms' entry rules and form links. The API checks every
   * form before writing any, answering 400 for one that is neither the study's own nor shared with
   * it, and writes them in one transaction, so a failure leaves every form as it was.
   */
  async saveStudyFormEnforcements(studyId: string, body: StudyFormEnforcementsPatch) {
    const uri = `study/${studyId}/form-enforcements`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, body);
  }

  async getForm(identifier: string, caseIdentifier?: string | null, studyId?: string, token?: string) {
    let uri = `form/${identifier}`;
    if (caseIdentifier) {
      uri += `?caseId=${caseIdentifier}`;
      if (studyId) {
        uri += `&studyId=${studyId}`;
      }
      if (token) {
        uri += `&token=${token}`;
      }
    } else if (studyId) {
      uri += `?studyId=${studyId}`;
      if (token) {
        uri += `&token=${token}`;
      }
    }
    const apiClient = signedApiClient('GET', uri);
    const formResponse = await apiClient.get(uri);
    return FormModel.mapFromBackend(formResponse.data);
  }

  async postForm(form: PostForm): Promise<PostFormResponse> {
    const uri = `form`;
    const apiClient = signedApiClient('POST', uri);
    return (await apiClient.post(uri, form)).data;
  }

  async patchForm(id: string, form: PostForm) {
    const uri = `form/${id}`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, form);
  }

  async postFormFile(id: string, file: PostFormFile): Promise<FormFileSignedResponse> {
    const uri = `${this.surveyEndpoint}/${id}/file`;
    const apiClient = signedApiClient('POST', uri);
    return (await apiClient.post(uri, file)).data;
  }

  async uploadFileToS3(file: File, presignedData: PresignedPost) {
    const fields = presignedData.fields;
    const form = new FormData();

    (Object.keys(fields) as Array<keyof PresignedPostFields>).forEach(key => {
      form.append(key, fields[key] as string);
    });

    form.append('file', file);

    const res = await axios({
      url: presignedData.url,
      method: 'POST',
      data: form
    });
    return res.data;
  }

  /**
   * Posts the file to S3, writing its percentage into `file.progress` and handing it to
   * `onProgress`. Aborting `signal` stops the post.
   */
  async uploadFileToS3WithProgress(
    file: BlobModel,
    presignedData: PresignedPost,
    { onProgress, signal }: { onProgress?: (percent: number) => void; signal?: AbortSignal } = {}
  ) {
    const fields = presignedData.fields;
    const form = new FormData();

    (Object.keys(fields) as Array<keyof PresignedPostFields>).forEach(key => {
      form.append(key, fields[key] as string);
    });

    form.append('file', file.file!);

    const res = await axios({
      url: presignedData.url,
      method: 'POST',
      data: form,
      signal,
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (progressEvent && progressEvent.lengthComputable) {
          const progress = Math.round((progressEvent.loaded / progressEvent.total!) * 100);
          file.progress = progress;
          onProgress?.(progress);
        }
      }
    });
    return res.data;
  }
}

export default new FormService();
